/**
 * @file Message 全局提示
 * @description 基于 antd message 封装，统一项目提示样式和类型图标
 */
import type React from 'react';
import {message as AntdMessage} from 'antd';
import type {ArgsProps, MessageType, NoticeType} from 'antd/es/message/interface';
import {createCx} from '@utils/cx';
import styles from './index.module.less';
import Icon from '@/components/common/Icon';
import type {IconName} from '@/components/common/Icon';

const cx = createCx(styles);

type MessageNoticeType = Extract<NoticeType, 'success' | 'info' | 'warning' | 'error' | 'loading'>;
type MessageArgsProps = Omit<ArgsProps, 'content' | 'icon' | 'type'> & {
    content: React.ReactNode;
};
type MessageJointContent = ArgsProps['content'] | MessageArgsProps;

type MessageOptions = (
    content: MessageJointContent,
    duration?: number | VoidFunction,
    onClose?: VoidFunction,
) => MessageType;
type MessageApi = Record<MessageNoticeType, MessageOptions>;

const MESSAGE_TYPES: MessageNoticeType[] = ['success', 'info', 'warning', 'error', 'loading'];

const DEFAULT_CLASS_NAMES = {
    root: cx('root'),
    icon: cx('icon'),
    content: cx('content'),
};

const DEFAULT_ICON_NAMES: Record<MessageNoticeType, IconName> = {
    success: 'AijBasicChenggong',
    info: 'AijBasicTishiblue',
    warning: 'AijBasicTishiyellow',
    error: 'AijBasicShibai',
    loading: 'AijBasicShuaxin',
};

function getIcon(type: MessageNoticeType): React.ReactNode {
    return <Icon name={DEFAULT_ICON_NAMES[type]} size={16} />;
}

function toArgsProps(content: MessageJointContent): MessageArgsProps {
    if (content && typeof content === 'object' && 'content' in content) {
        return content;
    }

    return {content};
}

function withMessageDefaults(type: MessageNoticeType, config: MessageArgsProps): ArgsProps {
    return {
        ...config,
        type,
        icon: getIcon(type),
        className: cx('notice'),
        classNames: DEFAULT_CLASS_NAMES,
    };
}

function createTypeOpen(type: MessageNoticeType): MessageOptions {
    return (content, duration, onClose) => {
        const config = toArgsProps(content);
        const mergedDuration = typeof duration === 'function' ? undefined : duration;
        const mergedOnClose = typeof duration === 'function' ? duration : onClose;

        return AntdMessage.open(
            withMessageDefaults(type, {
                ...config,
                duration: mergedDuration ?? 3,
                onClose: mergedOnClose,
            }),
        );
    };
}

const Message: MessageApi = MESSAGE_TYPES.reduce(
    (methods, type) => ({
        ...methods,
        [type]: createTypeOpen(type),
    }),
    {} as MessageApi,
);

export default Message;
